import fetch from './fetch';

export const globalHeaders = {};

/**
 * @class MyClass custom http error
 * @extends Error
 */
export class HTTPError extends Error {
  /**
   * constructor description
   * @param {String} message the error message
   * @param {Object} meta
   * @param {Number} meta.status the error status
   * @param {Object} [meta.rest] extra data from request
   */
  constructor(message, errorMessage, errorDetails, { status = null, url, ...rest } = {}) {
    super(message);

    this.name = 'HTTPError';
    this.status = status;
    this.errorMessage = errorMessage;
    this.errorDetails = errorDetails;
    this.url = url;
    this.metadata = { ...rest };
  }
}

export function setGlobalHeader(name, value) {
  globalHeaders[name] = value;
}

export function encodeQuery(params) {
  return encodeURIComponent(JSON.stringify(params));
}

export function is5xxError(code) {
  const reg = new RegExp(/^5\d{2}$/i);
  return reg.test(code);
}

function processError(r, contentType) {
  if (contentType && contentType.startsWith('application/json')) {
    return r.json().then(res => {
      const { status, ok, redirected, statusText, type, url, ...rest } = r;
      const message = res ? status.toString().concat('::').concat(res.message) : status;
      const errorMessage = res ? res.errorMessage : null;
      const errorDetails = res?.errorDetails || null;

      throw new HTTPError(message, errorMessage, errorDetails, {
        status,
        ok,
        redirected,
        statusText,
        type,
        url,
        ...rest
      });
    });
  }
  throw new Error();
}

function processResponse(r) {
  const contentType = r.headers.get('content-type');
  const newEtag = r.headers.get('ETag');

  if (
    r.status === 503 ||
    (is5xxError(r.status) &&
      (r.url.endsWith('/api2/v1/users/current') || r.url.endsWith('/api/v1/notifications/managable/outages')))
  ) {
    localStorage.clear();
    window.location.replace(`${window.location.origin}/outage`);
  }

  if (r.status === 204) {
    // 204 "No Content"
    return {
      data: null,
      httpMeta: {
        url: r.url,
        status: r.status,
        ...(newEtag && { ETAG: newEtag })
      }
    };
  }

  if (!r.ok) return processError(r, contentType);
  if (contentType) {
    if (contentType.startsWith('application/json')) {
      return r.json().then(data => ({
        data,
        httpMeta: {
          url: r.url,
          status: r.status,
          ...(newEtag && { ETAG: newEtag })
        }
      }));
    }
    if (contentType.startsWith('text/plain')) {
      return r.text();
    }
    if (
      contentType === 'image/png' ||
      contentType === 'application/octet-stream' ||
      contentType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    )
      return r.blob();
  }
  return r;
}

export default {
  post(url, payload, headers) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...globalHeaders, ...headers },
      body: payload ? JSON.stringify(payload) : null
    }).then(processResponse);
  },
  postFiles(url, payload, headers) {
    return fetch(url, {
      method: 'POST',
      headers: { ...globalHeaders, ...headers },
      body: payload
    }).then(processResponse);
  },
  get(url, headers) {
    const streamName = 'application/octet-stream';
    const isStream = headers?.accept === streamName;
    return fetch(url, { headers: { ...globalHeaders, ...headers } }).then(d => {
      if (isStream) return d;
      return processResponse(d);
    });
  },
  delete(url, payload, headers) {
    return fetch(url, {
      method: 'DELETE',
      headers: { ...globalHeaders, ...headers },
      body: JSON.stringify(payload)
    }).then(processResponse);
  },
  put(url, payload, headers) {
    return fetch(url, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...globalHeaders, ...headers },
      body: JSON.stringify(payload)
    }).then(processResponse);
  },
  patch(url, payload, headers) {
    return fetch(url, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...globalHeaders, ...headers },
      body: JSON.stringify(payload)
    }).then(processResponse);
  }
};
